import React, { useState } from 'react';
import styled from 'styled-components';
import { Tabs, Row, Col } from 'antd';
import { 
  FileTextOutlined, 
  PictureOutlined, 
  SoundOutlined, 
  VideoCameraOutlined 
} from '@ant-design/icons';
import ComingSoon from '../components/modules/ComingSoon';

const PageHeader = styled.div`
  background-color: #f6ffed;
  padding: 3rem 0;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #389e0d;
`;

const PageDescription = styled.p`
  font-size: 1.1rem;
  max-width: 800px;
  margin: 0 auto;
  color: #666;
`;

const SectionContainer = styled.section`
  padding: 3rem 0;
`;

const TabsContainer = styled.div`
  .ant-tabs-nav {
    margin-bottom: 2rem;
  }
  
  .ant-tabs-tab {
    font-size: 1.1rem;
    padding: 12px 0;
  }
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 2rem;
`;

const ImagePlaceholder = styled.div`
  background-color: #f0f2f5;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 3rem;
  color: #52c41a;
`;

const AudioPlayer = styled.div`
  background-color: #f0f2f5;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .icon {
    font-size: 2rem;
    color: #722ed1;
  }
  
  .info {
    flex-grow: 1;
  }
  
  .title {
    font-weight: 500;
    margin-bottom: 0.5rem;
  }
  
  .status {
    color: #666;
  }
`;

const VideoPlaceholder = styled.div`
  background-color: #f0f2f5;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  
  .icon {
    font-size: 3rem;
    color: #f5222d;
    margin-bottom: 1rem;
  }
  
  .title {
    font-weight: 500;
    margin-bottom: 0.5rem;
  }
  
  .status {
    color: #666;
  }
`;

const ArticlePlaceholder = styled.div`
  background-color: #f0f2f5;
  border-radius: 8px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  
  .title {
    font-size: 1.5rem;
    font-weight: 500;
    margin-bottom: 1rem;
    color: #1890ff;
  }
  
  .content {
    height: 100px;
    background-color: #e6e6e6;
    border-radius: 4px;
    margin-bottom: 1rem;
  }
  
  .status {
    color: #666;
    text-align: center;
    font-style: italic;
  }
`;

const AiCreation: React.FC = () => {
  const [activeTab, setActiveTab] = useState('text');
  
  const items = [
    {
      key: 'text',
      label: (
        <span>
          <FileTextOutlined />
          AI文字创作
        </span>
      ),
      children: (
        <div>
          <Row gutter={[24, 24]}>
            {[1, 2, 3].map((item) => (
              <Col xs={24} md={item === 3 ? 24 : 12} key={item}>
                <ArticlePlaceholder>
                  <div className="title">文章标题 #{item}</div>
                  <div className="content"></div>
                  <p className="status">文章内容创作中，敬请期待...</p>
                </ArticlePlaceholder>
              </Col>
            ))}
          </Row>
          <ComingSoon 
            title="更多AI文字创作" 
            description="更多精彩文章正在创作中..." 
          />
        </div>
      ),
    },
    {
      key: 'image',
      label: (
        <span>
          <PictureOutlined />
          AI图片生成
        </span>
      ),
      children: (
        <div>
          <ImageGrid>
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <ImagePlaceholder key={item}>
                <PictureOutlined />
              </ImagePlaceholder>
            ))}
          </ImageGrid>
          <ComingSoon 
            title="更多AI图像作品" 
            description="更多精彩图像作品正在创作中..." 
          />
        </div>
      ),
    },
    {
      key: 'audio',
      label: (
        <span>
          <SoundOutlined />
          AI语音合成
        </span>
      ),
      children: (
        <div>
          {[1, 2, 3].map((item) => (
            <AudioPlayer key={item}>
              <div className="icon">
                <SoundOutlined />
              </div>
              <div className="info">
                <div className="title">语音样本 #{item}</div>
                <div className="status">录制中，敬请期待...</div>
              </div>
            </AudioPlayer>
          ))}
          <ComingSoon 
            title="更多AI语音作品" 
            description="更多语音合成样本正在制作中..." 
          />
        </div>
      ),
    },
    {
      key: 'video',
      label: (
        <span>
          <VideoCameraOutlined />
          AI视频创作
        </span>
      ),
      children: (
        <div>
          {[1, 2].map((item) => (
            <VideoPlaceholder key={item}>
              <div className="icon">
                <VideoCameraOutlined />
              </div>
              <div className="title">视频项目 #{item}</div>
              <div className="status">制作中，敬请期待...</div>
            </VideoPlaceholder>
          ))}
          <ComingSoon 
            title="更多AI视频作品" 
            description="更多精彩视频内容正在创作中..." 
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader>
        <div className="container">
          <PageTitle>AI创作</PageTitle>
          <PageDescription>
            探索AI在文字、图像、语音和视频领域的创作能力
          </PageDescription>
        </div>
      </PageHeader>
      
      <SectionContainer>
        <div className="container">
          <TabsContainer>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={items}
              centered
            />
          </TabsContainer>
        </div>
      </SectionContainer>
    </>
  );
};

export default AiCreation; 